/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * The theme and rail width, applied to the document.
 *
 * The *record* of both lives with the person on the server, because a
 * preference someone sets on a laptop should hold on their desktop. What lives
 * here is the application of it: stamping the root element so the stylesheet
 * can see it, and mirroring the theme into this browser so the sign-in
 * screen -- which paints before anybody is authenticated -- opens in the right
 * colour rather than flashing the wrong one.
 */

type Mode = 'light' | 'dark';
type Rail = 'full' | 'mini';

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A browser set to block site data throws. Nothing here is worth failing
    // a render over -- the server still has the real answer.
  }
}

export function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Put the theme on the root element, and cache it for the next first paint. */
export function applyTheme(mode: Mode): void {
  document.documentElement.setAttribute('data-mode', mode);
  write('vellum-mode', mode);
}

/** Put the rail width on the root element, and cache it. */
export function applyRail(rail: Rail): void {
  if (rail === 'mini') document.documentElement.setAttribute('data-rail', 'mini');
  else document.documentElement.removeAttribute('data-rail');
  write('vellum-rail', rail);
}
